import {_} from 'lodash';
import {IOrganization, IOverride, ISystem, ISystemPermission, ISystemRole} from '../../../index';
import {Permission, SYSTEM_MEGA_EADM} from '../../../constants';
import {global, lm} from '../../../appContext';
import {getEadmRole} from '../../../util/misc';
import {nsUserPermissions} from './nsUserPermissions';
import {nsOverride} from '../../override/nsOverride';
import isOverridePendingDeletion = nsOverride.isOverridePendingDeletion;

/**
 * v2-only authorization + save validation for user permissions.
 * Deny reasons drive row-hover tooltips; keep auth checks separate from duplicate/role rules.
 */
export namespace nsUserPermissionPolicy {

    export type TDenyReason =
        | 'org-not-mutable'
        | 'not-super-employee'
        | 'system-not-searchable'
        | 'department-not-accessible'
        | 'mega-eadm-not-allowed'
        | 'system-definition-readonly'
        | 'override-not-interactive'
        | 'page-busy'
        | 'another-row-editing'
        | 'duplicate-permission'
        | 'multiple-permissions-same-company'
        | 'multiple-permissions-same-department'
        | 'missing-role'
        | 'missing-department';

    export interface ICapability {
        allowed: boolean;
        reasons: TDenyReason[];
    }

    /** Org-level inputs shared by every row after searchsystem / departments load. */
    export interface IOrgPolicyContext {
        organization: IOrganization;
        /** Systems from searchsystem (with roles). Empty / incomplete ⇒ not searchable. */
        searchableSystems: ISystem[];
        /** Department ids the actor may assign (from org department search). */
        availableDepartmentIds: number[];
        /** False when the actor has no login org for this company (legacy isMutable). */
        isOrgAccessible: boolean;
    }

    export interface IRowPolicyInput {
        systemPermission: ISystemPermission;
        /** Matched searchable system, or null when not in searchsystem. */
        searchableSystem: ISystem | null;
        /** Own override on this row (null for plain MP / Modified MP). */
        override: IOverride | null;
        /** Paired AP remove-override when this is a collapsed Modified MP. */
        pairedRemoveOverride: IOverride | null;
        /** Own override effectively removes this AP row. */
        isEffectivelyRemoved: boolean;
        /** Current managerOf department is soft-deleted (allows edit to fix). */
        departmentIsDeleted: boolean;
    }

    export interface IRowCapabilities {
        canEdit: ICapability;
        canDelete: ICapability;
        canRestore: ICapability;
        /** Roles allowed in the edit combobox for this system. */
        availableRoles: ISystemRole[];
    }

    export interface IPermissionIdentity {
        id: number;
        systemId: number;
        roleId: number;
        managerOf: number;
        isEffectivelyRemoved: boolean;
    }

    /**
     * Occupied system/role/department slots for combobox filtering (active rows + other drafts).
     * `roleId` / `managerOf` may be 0 when a draft has only picked a system (or role) so far.
     */
    export interface IOccupancySlot {
        systemId: number;
        roleId: number;
        managerOf: number;
    }

    const DENY_TOOLTIP_KEY: Record<TDenyReason, string> = {
        'org-not-mutable': 'user.permissions.deny.unauthorized',
        'not-super-employee': 'user.permissions.deny.unauthorized',
        'system-not-searchable': 'user.permissions.deny.unauthorized',
        'department-not-accessible': 'user.permissions.deny.unauthorized',
        'mega-eadm-not-allowed': 'user.permissions.deny.unauthorized',
        'system-definition-readonly': 'user.permissions.deny.integration',
        'override-not-interactive': 'user.permissions.deny.unauthorized',
        'page-busy': 'user.permissions.policy.page-busy',
        'another-row-editing': 'user.permissions.policy.another-row-editing',
        'duplicate-permission': 'user.permissions.deny.alreadyassigned',
        'multiple-permissions-same-company': 'user.permissions.deny.onecollection',
        'multiple-permissions-same-department': 'user.permissions.deny.departmentonce',
        'missing-role': 'user.permissions.deny.invalid',
        'missing-department': 'user.permissions.deny.departmentmissing',
    };

    const RESTORE_BLOCKED_BODY_KEY: Partial<Record<TDenyReason, string>> = {
        'duplicate-permission': 'user.permissions.restore.blocked.alreadyassigned',
        'multiple-permissions-same-company': 'user.permissions.restore.blocked.onecollection',
        'multiple-permissions-same-department': 'user.permissions.restore.blocked.departmentonce',
    };

    function allow(): ICapability {
        return {allowed: true, reasons: []};
    }

    function deny(...reasons: TDenyReason[]): ICapability {
        return {allowed: false, reasons};
    }

    function mergeCapabilities(...parts: ICapability[]): ICapability {
        const reasons = _.uniq(_.flatten(parts.filter(p => !p.allowed).map(p => p.reasons)));
        return reasons.length ? deny(...reasons) : allow();
    }

    /** Translation key for the first deny reason (for `tt` / language-reactive UI). */
    export function denyTooltipKey(reasons: TDenyReason[]): string {
        const first = reasons[0];
        if (!first) {
            return '';
        }
        return DENY_TOOLTIP_KEY[first] ?? 'user.permissions.deny.unauthorized';
    }

    /** Tooltip text for the first deny reason (already translated). */
    export function tooltipForReasons(reasons: TDenyReason[]): string {
        const key = denyTooltipKey(reasons);
        return key ? (lm.t(key) ?? key) : '';
    }

    /** Info-dialog body for occupancy-blocked restore (first matching reason). */
    export function restoreBlockedBodyKey(reasons: TDenyReason[]): string {
        const first = reasons[0];
        if (!first) {
            return '';
        }
        return RESTORE_BLOCKED_BODY_KEY[first] ?? denyTooltipKey(reasons);
    }

    export function buildOrgContext(
        organization: IOrganization,
        searchableSystems: ISystem[] | null,
        availableDepartmentIds: number[],
    ): IOrgPolicyContext {
        return {
            organization,
            searchableSystems: searchableSystems ?? [],
            availableDepartmentIds,
            isOrgAccessible: !!global.login.getOrganizationById(organization.organizationid),
        };
    }

    /** Shared actor/org gates (org access + SuperEmployee). Mega rank is per-row via isMegaEadmAllowed. */
    export function evaluateActorOrgGates(org: IOrgPolicyContext): ICapability {
        const reasons: TDenyReason[] = [];
        if (!org.isOrgAccessible) {
            reasons.push('org-not-mutable');
        }
        const loginOrg = global.login.getOrganizationById(org.organization.organizationid);
        if (getEadmRole(loginOrg) < Permission.SuperEmployee) {
            reasons.push('not-super-employee');
        }
        return reasons.length ? deny(...reasons) : allow();
    }

    export function isSystemSearchable(org: IOrgPolicyContext, system: ISystem | null | undefined): boolean {
        if (!system?.id) {
            return true;
        }
        return org.searchableSystems.some(s => s.id == system.id);
    }

    export function hasDepartmentAccess(
        org: IOrgPolicyContext,
        systemPermission: ISystemPermission,
        departmentIsDeleted: boolean,
    ): boolean {
        if (departmentIsDeleted) {
            return true;
        }
        if (nsUserPermissions.isLegalUnitScope(systemPermission)) {
            return true;
        }
        const recursive = !!(systemPermission.role?.recursive ?? systemPermission.recursive);
        const managerOf = systemPermission.managerOf ?? 0;
        if (!recursive || !managerOf) {
            return true;
        }
        return org.availableDepartmentIds.includes(managerOf);
    }

    /**
     * Mega eAdm row mutability (legacy AbstractSystemPermission.isMutable):
     * actor’s Mega rank on this company must be ≥ this row’s roleenum.
     * Non-Mega, or falsy ranks (None = 0), are allowed.
     */
    export function isMegaEadmAllowed(
        organization: IOrganization,
        system: ISystem | null | undefined,
        role?: ISystemRole | null,
    ): boolean {
        if (system?.name != SYSTEM_MEGA_EADM) {
            return true;
        }
        const eadmRole = getEadmRole(global.login.getOrganizationById(organization.organizationid));
        const rowRole = role?.roleenum;
        if (eadmRole && rowRole) {
            return eadmRole >= rowRole;
        }
        return true;
    }

    /**
     * Roles offered when adding/editing: searchable system’s roles, Mega eAdm capped by
     * actor eAdm level (< SuperAdmin and ≤ actor). Falls back to the current role only
     * when the system is not searchable.
     */
    export function availableRolesForSystem(
        searchableSystem: ISystem | null,
        fallbackRole: ISystemRole | null | undefined,
    ): ISystemRole[] {
        if (!searchableSystem?.roles?.length) {
            return fallbackRole ? [fallbackRole] : [];
        }
        if (searchableSystem.name == SYSTEM_MEGA_EADM) {
            const actorLevel = getEadmRole(global.login.getOrganizationById(searchableSystem.companyid));
            return searchableSystem.roles
                .filter(r => r.roleenum < Permission.SuperAdmin && r.roleenum <= actorLevel)
                .reverse();
        }
        return _.sortBy(searchableSystem.roles, (r: ISystemRole) => r.name.toLowerCase());
    }

    /** Whether an AP’s override still allows mutate (legacy canInteract; searchable is a separate gate). */
    export function canInteractWithAutomaticOverride(override: IOverride | null): boolean {
        return !override?.deletedbyid;
    }

    export function evaluateRow(org: IOrgPolicyContext, row: IRowPolicyInput): IRowCapabilities {
        const actorOrg = evaluateActorOrgGates(org);
        const system = row.searchableSystem ?? row.systemPermission.system ?? null;
        const searchable = isSystemSearchable(org, system);
        const deptOk = hasDepartmentAccess(org, row.systemPermission, row.departmentIsDeleted);
        const megaOk = isMegaEadmAllowed(org.organization, system, row.systemPermission.role);
        const definitionReadOnly = nsUserPermissions.isSystemDefinitionReadOnly(system);
        const roles = availableRolesForSystem(row.searchableSystem, row.systemPermission.role);

        const sharedReasons: TDenyReason[] = [];
        if (!actorOrg.allowed) {
            sharedReasons.push(...actorOrg.reasons);
        }
        if (!deptOk) {
            sharedReasons.push('department-not-accessible');
        }
        if (!megaOk) {
            sharedReasons.push('mega-eadm-not-allowed');
        }
        if (definitionReadOnly) {
            sharedReasons.push('system-definition-readonly');
        }

        const isModifiedPair = !!row.pairedRemoveOverride && !!row.systemPermission.manual;
        const isManual = !!row.systemPermission.manual || isModifiedPair;

        if (isManual) {
            // MP (incl. Modified): edit/delete need searchable system; never restore.
            const mpReasons = [...sharedReasons];
            if (!searchable) {
                mpReasons.push('system-not-searchable');
            }
            const mutate = mpReasons.length ? deny(...mpReasons) : allow();
            return {
                canEdit: mutate,
                canDelete: mutate,
                canRestore: deny('override-not-interactive'),
                availableRoles: roles,
            };
        }

        // Soft-deleted override: Restore → restoreoverride (not edit/delete / deleteoverride).
        if (isOverridePendingDeletion(row.override)) {
            const restoreReasons = [...sharedReasons];
            if (!searchable) {
                restoreReasons.push('system-not-searchable');
            }
            return {
                canEdit: deny('override-not-interactive'),
                canDelete: deny('override-not-interactive'),
                canRestore: restoreReasons.length ? deny(...restoreReasons) : allow(),
                availableRoles: roles,
            };
        }

        // Automatic — edit/delete/restore all need searchable (legacy isMutable gate).
        const interactOk = canInteractWithAutomaticOverride(row.override);
        const apReasons = [...sharedReasons];
        if (!interactOk) {
            apReasons.push('override-not-interactive');
        }
        if (!searchable) {
            apReasons.push('system-not-searchable');
        }
        const apMutate = apReasons.length ? deny(...apReasons) : allow();

        if (row.isEffectivelyRemoved) {
            return {
                canEdit: deny('override-not-interactive'),
                canDelete: deny('override-not-interactive'),
                canRestore: apMutate,
                availableRoles: roles,
            };
        }

        return {
            canEdit: apMutate,
            canDelete: apMutate,
            canRestore: deny('override-not-interactive'),
            availableRoles: roles,
        };
    }

    /** Page-level disables layered on top of row policy (busy / exclusive edit). */
    export function withPageGates(
        capability: ICapability,
        page: {isBusy: boolean; anotherRowEditing: boolean},
    ): ICapability {
        const extra: TDenyReason[] = [];
        if (page.isBusy) {
            extra.push('page-busy');
        }
        if (page.anotherRowEditing) {
            extra.push('another-row-editing');
        }
        return extra.length ? mergeCapabilities(capability, deny(...extra)) : capability;
    }

    /**
     * Save-time validation (legacy Organization duplicate / multiplerole* rules).
     * `siblings` should exclude effectively removed rows; `draft` is the row being saved.
     */
    export function validatePermissionDraft(
        draft: IPermissionIdentity,
        siblings: IPermissionIdentity[],
        system: ISystem | null | undefined,
    ): ICapability {
        if (!draft.roleId) {
            return deny('missing-role');
        }
        const recursive = !!system?.roles?.find(r => r.id == draft.roleId)?.recursive;
        if (recursive && !draft.managerOf) {
            return deny('missing-department');
        }

        const active = siblings.filter(s => !s.isEffectivelyRemoved && s.id != draft.id);

        if (active.some(s =>
            s.systemId == draft.systemId
            && s.roleId == draft.roleId
            && (s.managerOf ?? 0) == (draft.managerOf ?? 0)
        )) {
            return deny('duplicate-permission');
        }

        if (system && !system.multiplerolesamecompany) {
            if (active.some(s => s.systemId == draft.systemId)) {
                return deny('multiple-permissions-same-company');
            }
        }

        if (system && !system.multiplerolesamedepartment && draft.managerOf) {
            if (active.some(s => s.systemId == draft.systemId && (s.managerOf ?? 0) == draft.managerOf)) {
                return deny('multiple-permissions-same-department');
            }
        }

        return allow();
    }

    /** Convenience: identity snapshot from a permission + optional live role/dept. */
    export function toIdentity(
        sp: ISystemPermission,
        opts?: {
            roleId?: number;
            managerOf?: number;
            isEffectivelyRemoved?: boolean;
        },
    ): IPermissionIdentity {
        return {
            id: sp.id ?? 0,
            systemId: sp.system?.id ?? 0,
            roleId: opts?.roleId ?? sp.role?.id ?? 0,
            managerOf: opts?.managerOf ?? sp.managerOf ?? 0,
            isEffectivelyRemoved: opts?.isEffectivelyRemoved ?? false,
        };
    }

    /**
     * Systems available in an Add draft picker.
     * Mega role caps are applied in the role picker (`availableRolesForSystem`), not here.
     * When `multiplerolesamecompany` is false, systems with any occupied slot are hidden
     * (except `keepSystemId` for the current draft selection).
     */
    export function filterSystemsForPicker(
        systems: ISystem[],
        occupied: IOccupancySlot[],
        _organization: IOrganization,
        keepSystemId?: number | null,
    ): ISystem[] {
        return systems.filter(system => {
            if (nsUserPermissions.isSystemDefinitionReadOnly(system)) {
                return false;
            }
            if (keepSystemId != null && system.id == keepSystemId) {
                return true;
            }
            if (!system.multiplerolesamecompany && occupied.some(o => o.systemId == system.id)) {
                return false;
            }
            return true;
        });
    }

    /**
     * Roles for add/edit pickers: hide exact non-recursive duplicates and, when
     * `multiplerolesamecompany` is false, all roles if the system is already occupied.
     * Always keeps `keepRoleId` (current selection while editing).
     */
    export function filterRolesForPicker(
        roles: ISystemRole[],
        system: ISystem,
        occupied: IOccupancySlot[],
        keepRoleId?: number | null,
    ): ISystemRole[] {
        const systemOccupied = occupied.some(o => o.systemId == system.id);
        return roles.filter(role => {
            if (keepRoleId != null && role.id == keepRoleId) {
                return true;
            }
            if (!system.multiplerolesamecompany && systemOccupied) {
                return false;
            }
            if (!role.recursive) {
                if (occupied.some(o =>
                    o.systemId == system.id
                    && o.roleId == role.id
                    && (o.managerOf ?? 0) == 0
                )) {
                    return false;
                }
            }
            return true;
        });
    }

    /**
     * Add/edit role combobox data: Mega rank cap via `availableRolesForSystem`, then occupancy.
     */
    export function rolesForPicker(
        system: ISystem | null,
        occupied: IOccupancySlot[],
        keepRoleId?: number | null,
        fallbackRole?: ISystemRole | null,
    ): ISystemRole[] {
        if (!system) {
            return fallbackRole ? [fallbackRole] : [];
        }
        return filterRolesForPicker(
            availableRolesForSystem(system, fallbackRole),
            system,
            occupied,
            keepRoleId,
        );
    }

    /**
     * Departments for recursive roles: hide exact system+role+dept duplicates, and when
     * `multiplerolesamedepartment` is false any dept already used on that system.
     */
    export function filterDepartmentsForPicker<T extends {id: number}>(
        departments: T[],
        system: ISystem,
        role: ISystemRole | null | undefined,
        occupied: IOccupancySlot[],
        keepDepartmentId?: number | null,
    ): T[] {
        if (!role) {
            return departments;
        }
        const {takenOnSystem, takenOnSystemRole} = collectOccupiedDepartmentIds(
            occupied,
            system.id,
            role.id,
        );

        return departments.filter(dept => {
            if (keepDepartmentId != null && dept.id == keepDepartmentId) {
                return true;
            }
            if (!system.multiplerolesamedepartment && takenOnSystem.has(dept.id)) {
                return false;
            }
            return !takenOnSystemRole.has(dept.id);
        });
    }

    /**
     * Whether Copy can offer a completable draft for this system under occupancy.
     * Caller includes the source slot only when it still counts as taken (active row).
     * Removed rows omit the source so the freed slot can be reused.
     */
    export function hasAlternateCopySlot(
        system: ISystem,
        occupied: IOccupancySlot[],
        departments: {id: number}[],
        sourceRole: ISystemRole,
    ): boolean {
        // Second permission on this system would always fail save.
        if (!system.multiplerolesamecompany && occupied.some(o => o.systemId == system.id)) {
            return false;
        }
        const availableRoles = filterRolesForPicker(
            availableRolesForSystem(system, sourceRole),
            system,
            occupied,
            null,
        );
        // Non-recursive copy seeds system only — any free role (including the source role) works.
        if (!sourceRole.recursive) {
            return availableRoles.length > 0;
        }
        // Recursive copy keeps the role; need at least one free department.
        if (!availableRoles.some(r => r.id == sourceRole.id)) {
            return false;
        }
        const {takenOnSystem, takenOnSystemRole} = collectOccupiedDepartmentIds(
            occupied,
            system.id,
            sourceRole.id,
        );
        // Early-exit scan — do not allocate a filtered copy of a tens-of-thousands dept list.
        for (const dept of departments) {
            if (!system.multiplerolesamedepartment && takenOnSystem.has(dept.id)) {
                continue;
            }
            if (takenOnSystemRole.has(dept.id)) {
                continue;
            }
            return true;
        }
        return false;
    }

    /** Re-export pairing helper for callers that already use permissions helpers. */
    export const sameSystemRoleDepartment = nsUserPermissions.sameSystemRoleDepartment;

    /** Reuse the previous array when ids are unchanged so combobox `data` does not notify. */
    export function reuseArrayIfSameIds<T extends {id: number}>(previous: T[], next: T[]): T[] {
        if (previous === next) {
            return previous;
        }
        if (previous.length != next.length) {
            return next;
        }
        for (let i = 0; i < previous.length; i++) {
            if (previous[i]!.id != next[i]!.id) {
                return next;
            }
        }
        return previous;
    }

    /** Collect occupied department ids once before scanning a potentially large department list. */
    function collectOccupiedDepartmentIds(
        occupied: IOccupancySlot[],
        systemId: number,
        roleId: number,
    ): {takenOnSystem: Set<number>; takenOnSystemRole: Set<number>} {
        const takenOnSystem = new Set<number>();
        const takenOnSystemRole = new Set<number>();
        for (const slot of occupied) {
            if (slot.systemId != systemId) {
                continue;
            }
            const departmentId = slot.managerOf ?? 0;
            takenOnSystem.add(departmentId);
            if (slot.roleId == roleId) {
                takenOnSystemRole.add(departmentId);
            }
        }
        return {takenOnSystem, takenOnSystemRole};
    }
}
